import React, { useEffect, useId, useState } from "react";

// COMPONENTS
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import RowEmailTemplate from "./rowEmailTemplate/rowEmailTemplate";

// UTILS/API
import { getDefaultMessageResultsFound, getTranslateByKey, isNullUndefinedEmpty } from "@/main/services/utils";
import { templateEmailNotificationCompanyApi } from "@/main/api";

import { SearchgridTemplateRequest } from "@/main/models/request/templateEmailNotificationCompanyRequest";
import * as StylesShared from "@/main/components/shared/styles";
import * as S from "./styles";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import ModalEmailTemplatesDetails from "../modalEmailTemplatesDetails/modalEmailTemplatesDetails";
import { SearchgridTemplateResponse } from "@/main/models/response/templateEmailNotificationCompanyResponse";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";

interface IGridEmailTemplateProps {
	gridData: IGridData;
	formFilters: SearchgridTemplateRequest | null;
	setGridData: React.Dispatch<React.SetStateAction<IGridData>>;
}

export interface IGridData {
	isLoading: boolean;
	totalItems: number;
	data: SearchgridTemplateResponse[];
}

const INITIAL_MODAL_EMAIL_TEMPLATES_DETAILS_STATE = {
	openModal: false as boolean,
	item: null as SearchgridTemplateResponse | null,
};

export const INITIAL_GRID_STATE: IGridData = {
	isLoading: false,
	totalItems: 0,
	data: [],
};

export type IModalEmailTemplatesDetails = typeof INITIAL_MODAL_EMAIL_TEMPLATES_DETAILS_STATE;

function GridSearchEmailTemplate({ gridData, formFilters, setGridData }: IGridEmailTemplateProps) {
	const scrollTargetId = `email-templates-grid-${useId()}`;
	const [pageIndex, setPageIndex] = useState(PaginationEnum.PageIndexIntial);
	const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState(false);
	const [hasShowMore, setHasShowMore] = useState(false);
	const [modalEmailTemplatesDetails, setModalEmailTemplatesDetails] = useState<IModalEmailTemplatesDetails>(
		INITIAL_MODAL_EMAIL_TEMPLATES_DETAILS_STATE,
	);

	const rowHeaders = [
		getTranslateByKey("information.insurer"),
		getTranslateByKey("information.subject"),
		getTranslateByKey("information.templateType"),
		getTranslateByKey("information.systemApplication"),
		<StylesShared.RowHeaderTextCenter key="4">
			{getTranslateByKey("information.actions")}
		</StylesShared.RowHeaderTextCenter>,
	];

	const titleGrid = getDefaultMessageResultsFound(gridData.data.length, gridData.totalItems);

	useEffect(() => {
		setHasShowMore(gridData.data.length < gridData.totalItems);
	}, [gridData.data, gridData.totalItems]);

	async function onLoadInfiniteScroll(request: SearchgridTemplateRequest) {
		setIsLoadingInfiniteScroll(true);

		const requestGrid: SearchgridTemplateRequest = {
			...request,
			pageIndex: request.pageIndex ?? PaginationEnum.PageIndexIntial,
			pageSize: request.pageSize ?? PaginationEnum.PageSize30,
		};

		templateEmailNotificationCompanyApi
			.getSearchGridTemplate(requestGrid)
			.then((response) => {
				let resultData: any[] = [];
				let resultTotalItems: number = 0;
				if (response.data) {
					resultData = response.data.results;
					resultTotalItems = response.data.totalNumberOfRecords;
				}

				if (gridData.data.length > 0) {
					resultData = [...gridData.data, ...resultData];
				}

				setGridData(() => ({
					isLoading: false,
					totalItems: resultTotalItems,
					data: resultData,
				}));

				setHasShowMore(response.data.results.length < response.data.totalNumberOfRecords);
			})
			.finally(() => setIsLoadingInfiniteScroll(false));
	}

	function openModalDetail(item: SearchgridTemplateResponse) {
		setModalEmailTemplatesDetails({
			openModal: true,
			item,
		});
	}

	function onLoadSearchGrid() {
		setPageIndex((prev) => prev + 1);
		const formFiltersOnLoadSearchGrid = formFilters;
		if (formFiltersOnLoadSearchGrid) {
			const updatedFormFilters = {
				...formFiltersOnLoadSearchGrid,
				pageIndex: pageIndex + 1,
			};
			onLoadInfiniteScroll(updatedFormFilters);
		}
	}

	if (gridData.isLoading) return <LoadingComponent />;
	if (gridData.data.length === 0 && formFilters === null) return null;
	if (gridData.data.length === 0 && formFilters !== null) return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
	if (gridData.data.length === 0 && !gridData.isLoading) return null;

	return (
		<>
			<S.Container>
				<BoxGrid
					id={scrollTargetId}
					maxHeight="50vh"
					title={titleGrid}
					rowHeaders={rowHeaders}
					infiniteScrollConfig={{
						dataLength: gridData.data.length,
						fetchData: () => onLoadSearchGrid(),
						hasMore: hasShowMore,
						isLoading: isLoadingInfiniteScroll,
						idScrollTarget: scrollTargetId,
					}}
				>
					{gridData.data.map((item) => (
						<RowEmailTemplate
							key={item.guidTemplateEmailNotificationCompany}
							emailTemplate={item}
							openModalDetail={openModalDetail}
						/>
					))}
				</BoxGrid>
			</S.Container>

			<KogModalSideComponent
				open={modalEmailTemplatesDetails.openModal}
				closeModal={() => setModalEmailTemplatesDetails(INITIAL_MODAL_EMAIL_TEMPLATES_DETAILS_STATE)}
				title={getTranslateByKey("information.templateDetail")}
				titleModalReducer={getTranslateByKey("information.templateDetail")}
			>
				{modalEmailTemplatesDetails.openModal && !isNullUndefinedEmpty(modalEmailTemplatesDetails.item) && (
					<ModalEmailTemplatesDetails
						isOpen={modalEmailTemplatesDetails.openModal}
						setIsOpen={(value: boolean) =>
							setModalEmailTemplatesDetails((prev) => ({
								...prev,
								openModal: value,
							}))
						}
						templateEmail={modalEmailTemplatesDetails.item}
						guidCountry={formFilters?.guidCountry}
					/>
				)}
			</KogModalSideComponent>
		</>
	);
}

export default GridSearchEmailTemplate;
